import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import * as XLSX from 'xlsx'

const fmt = n => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtM = n => { const v = Number(n || 0); if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M'; if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K'; return v.toFixed(2) }

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
  </svg>
)
const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round"/>
  </svg>
)

const TYPE_COLORS = {
  standard:  'bg-[#DDD9D0] text-[#1A7A4A]',
  complex:   'bg-[#DDD9D0] text-[#1A7A4A]',
  equipment: 'bg-amber-100 text-amber-700',
  service:   'bg-[#DDD9D0] text-[#1C1A18]',
}

export default function POReconciliation({ initProject }) {
  const [projects, setProjects]       = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [rows, setRows]               = useState([])
  const [loading, setLoading]         = useState(false)
  const [search, setSearch]           = useState('')
  const [typeFilter, setTypeFilter]   = useState('all')
  const [diffFilter, setDiffFilter]   = useState('all')

  useEffect(() => {
    supabase.from('projects').select('id,name,code').order('name').then(({ data }) => {
      const list = data || []
      setProjects(list)
      if (initProject) {
        const found = list.find(p => p.id === initProject.id) || initProject
        setSelectedProject(found)
        load(found.id)
      }
    })
  }, [initProject?.id])

  const load = async (projectId) => {
    setLoading(true)
    setRows([])

    // 1) POs
    const { data: pos } = await supabase
      .from('purchase_orders')
      .select('id, po_number, vendor, po_type, cost_type, sub_code, total_exc_vat, total_inc_vat, status')
      .eq('project_id', projectId)
      .order('po_number', { ascending: true })

    if (!pos?.length) { setLoading(false); return }

    const poIds = pos.map(p => p.id)

    // 2) IPC: ipc_certificates → subcontracts.po_id → ipc_line_items
    // Chain: ipc_line_items.ipc_id → ipc_certificates.subcontract_id → subcontracts.po_id
    const { data: subcontracts } = await supabase
      .from('subcontracts')
      .select('id, po_id')
      .eq('project_id', projectId)
      .not('po_id', 'is', null)

    const subToPoMap = {}
    for (const s of subcontracts || []) subToPoMap[s.id] = s.po_id

    const subIds = Object.keys(subToPoMap)
    let ipcCerts = []
    if (subIds.length) {
      const { data } = await supabase
        .from('ipc_certificates')
        .select('id, subcontract_id')
        .in('subcontract_id', subIds)
      ipcCerts = data || []
    }

    const certToPoMap = {}
    for (const c of ipcCerts) {
      const poId = subToPoMap[c.subcontract_id]
      if (poId) certToPoMap[c.id] = poId
    }

    const certIds = Object.keys(certToPoMap)
    let ipcLines = []
    if (certIds.length) {
      const { data } = await supabase
        .from('ipc_line_items')
        .select('ipc_id, invoiced_amount, this_period_qty, unit_rate')
        .in('ipc_id', certIds)
      ipcLines = data || []
    }

    // 3) Store issues — via po_id OR po_line_item_id
    const { data: storeIssuesAll } = await supabase
      .from('store_issues')
      .select('po_id, po_line_item_id, qty_issued, unit_rate')
      .eq('project_id', projectId)

    // Map po_line_item_id → po_id
    const { data: poLineItems } = await supabase
      .from('po_line_items')
      .select('id, po_id')
      .in('po_id', poIds)
    const lineToPoMap = {}
    for (const l of poLineItems || []) lineToPoMap[l.id] = l.po_id

    // 4) Equipment costs grouped by po_id
    const { data: eqpCosts } = await supabase
      .from('equipment_costs')
      .select('po_id, invoiced_amount')
      .in('po_id', poIds)
      .eq('status', 'approved')

    // Build maps
    const ipcMap = {}
    for (const l of ipcLines) {
      const poId = certToPoMap[l.ipc_id]
      if (!poId) continue
      const amt = parseFloat(l.invoiced_amount) || (parseFloat(l.this_period_qty) || 0) * (parseFloat(l.unit_rate) || 0)
      ipcMap[poId] = (ipcMap[poId] || 0) + amt
    }
    const storeMap = {}
    for (const s of storeIssuesAll || []) {
      const poId = s.po_id || lineToPoMap[s.po_line_item_id]
      if (!poId) continue
      const amt = (parseFloat(s.qty_issued) || 0) * (parseFloat(s.unit_rate) || 0)
      storeMap[poId] = (storeMap[poId] || 0) + amt
    }
    const eqpMap = {}
    for (const e of eqpCosts || []) {
      eqpMap[e.po_id] = (eqpMap[e.po_id] || 0) + (parseFloat(e.invoiced_amount) || 0)
    }

    const built = pos.map(po => {
      const ipc    = ipcMap[po.id]   || 0
      const store  = storeMap[po.id] || 0
      const eqp    = eqpMap[po.id]   || 0
      const totalInvoiced = ipc + store + eqp
      const committed = parseFloat(po.total_exc_vat) || 0
      const diff = committed - totalInvoiced
      return { ...po, ipc, store, eqp, totalInvoiced, committed, diff }
    })

    setRows(built)
    setLoading(false)
  }

  const handleProject = (p) => {
    setSelectedProject(p)
    setRows([])
    setSearch('')
    load(p.id)
  }

  const filtered = useMemo(() => {
    return rows.filter(r => {
      const s = search.toLowerCase()
      const matchSearch = !search ||
        r.po_number?.toLowerCase().includes(s) ||
        r.vendor?.toLowerCase().includes(s) ||
        r.sub_code?.toLowerCase().includes(s)
      const matchType = typeFilter === 'all' || r.po_type === typeFilter
      const matchDiff =
        diffFilter === 'all'           ? true :
        diffFilter === 'has_invoiced'  ? r.totalInvoiced > 0 :
        diffFilter === 'zero_invoiced' ? r.totalInvoiced === 0 :
        diffFilter === 'mismatch'      ? Math.abs(r.diff) > 1 : true
      return matchSearch && matchType && matchDiff
    })
  }, [rows, search, typeFilter, diffFilter])

  const totals = useMemo(() => ({
    committed:     filtered.reduce((s, r) => s + r.committed, 0),
    totalInvoiced: filtered.reduce((s, r) => s + r.totalInvoiced, 0),
    ipc:           filtered.reduce((s, r) => s + r.ipc, 0),
    store:         filtered.reduce((s, r) => s + r.store, 0),
    eqp:           filtered.reduce((s, r) => s + r.eqp, 0),
    diff:          filtered.reduce((s, r) => s + r.diff, 0),
  }), [filtered])

  const exportExcel = () => {
    const data = filtered.map(r => ({
      'PO Number':       r.po_number,
      'Supplier':        r.vendor,
      'Sub Code':        r.sub_code || '',
      'Type':            r.po_type,
      'Cost Type':       r.cost_type,
      'Status':          r.status,
      'Committed (Exc.VAT)': r.committed,
      'IPC Invoiced':    r.ipc,
      'Store Issued':    r.store,
      'Equipment Cost':  r.eqp,
      'Total Invoiced':  r.totalInvoiced,
      'Remaining':       r.diff,
    }))
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'PO Reconciliation')
    XLSX.writeFile(wb, `PO_Reconciliation_${selectedProject?.code || ''}.xlsx`)
  }

  return (
    <div style={{minHeight:"100vh",background:"#F3F4F6",fontFamily:"inherit"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #E5E7EB",padding:"18px 24px 14px"}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16}}>
          <div>
            <h1 className="text-xl font-extrabold text-[#0F1017] tracking-tight">PO Reconciliation</h1>
            <p className="text-xs text-[#6B7280] mt-0.5">مقارنة قيم الـ POs مع الفعلي (IPC + Store + Equipment)</p>
          </div>
          <div className="flex gap-2">
            {selectedProject && !loading && rows.length > 0 && (
              <>
                <button onClick={() => load(selectedProject.id)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA] text-[#6B7280]">
                  <RefreshIcon/> Refresh
                </button>
                <button onClick={exportExcel}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm bg-[#1A7A4A] text-white rounded-lg hover:bg-[#15693F]">
                  <DownloadIcon/> Export Excel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:16}}>

        {/* Project Select */}
        {projects.length > 0 && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-3">Select Project</p>
            <div className="flex flex-wrap gap-2">
              {projects.map(p => (
                <button key={p.id} onClick={() => handleProject(p)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                    selectedProject?.id === p.id
                      ? 'bg-[#1C1A18] text-white border-[#1A7A4A]'
                      : 'bg-white text-[#2E2C29] border-[#E5E7EB] hover:border-[#1A7A4A]/50'
                  }`}>
                  {p.name} <span className="opacity-60 text-xs ml-1">{p.code}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-20 text-[#9CA3AF] text-sm">Loading PO data...</div>
        )}

        {!loading && rows.length > 0 && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <p className="text-xs text-[#6B7280]">Total POs</p>
                <p className="text-xl font-extrabold text-[#0F1017] tracking-tight mt-1">{filtered.length}</p>
                <p className="text-xs text-[#9CA3AF] mt-0.5">of {rows.length} total</p>
              </div>
              <div className="bg-[#1C1A18] rounded-xl p-4 text-white">
                <p className="text-xs text-[#9CA3AF]">Committed (Exc.VAT)</p>
                <p className="text-xl font-bold mt-1">{fmtM(totals.committed)}</p>
                <p className="text-xs text-indigo-300 mt-0.5">{fmt(totals.committed)}</p>
              </div>
              <div className="bg-[#1A7A4A] rounded-xl p-4 text-white">
                <p className="text-xs text-emerald-200">Total Invoiced / Issued</p>
                <p className="text-xl font-bold mt-1">{fmtM(totals.totalInvoiced)}</p>
                <p className="text-xs text-emerald-300 mt-0.5">{fmt(totals.totalInvoiced)}</p>
              </div>
              <div className={`rounded-xl p-4 text-white ${totals.diff < 0 ? 'bg-red-600' : 'bg-[#2E2C29]'}`}>
                <p className="text-xs opacity-70">Remaining</p>
                <p className="text-xl font-bold mt-1">{fmtM(totals.diff)}</p>
                <p className="text-xs opacity-60 mt-0.5">{fmt(totals.diff)}</p>
              </div>
            </div>

            {/* Sub-totals */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#EDE7DA] rounded-xl border border-[#E5E7EB] p-3 flex justify-between items-center">
                <span className="text-xs text-[#6B7280]">IPC Invoiced</span>
                <span className="text-sm font-semibold text-[#1A7A4A]">{fmtM(totals.ipc)}</span>
              </div>
              <div className="bg-[#EDE7DA] rounded-xl border border-[#E5E7EB] p-3 flex justify-between items-center">
                <span className="text-xs text-[#6B7280]">Store Issued</span>
                <span className="text-sm font-semibold text-[#1A7A4A]">{fmtM(totals.store)}</span>
              </div>
              <div className="bg-[#EDE7DA] rounded-xl border border-[#E5E7EB] p-3 flex justify-between items-center">
                <span className="text-xs text-[#6B7280]">Equipment Cost</span>
                <span className="text-sm font-semibold text-amber-600">{fmtM(totals.eqp)}</span>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-48">
                <span className="absolute left-3 top-2.5 text-[#9CA3AF]"><SearchIcon/></span>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search PO number, supplier, sub code..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1A7A4A]"/>
              </div>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1A7A4A] bg-white">
                <option value="all">All Types</option>
                <option value="standard">Standard</option>
                <option value="complex">Complex</option>
                <option value="equipment">Equipment</option>
                <option value="service">Service</option>
              </select>
              <select value={diffFilter} onChange={e => setDiffFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1A7A4A] bg-white">
                <option value="all">All POs</option>
                <option value="has_invoiced">Has Invoiced</option>
                <option value="zero_invoiced">Zero Invoiced</option>
                <option value="mismatch">Mismatch</option>
              </select>
              <span className="self-center text-sm text-[#9CA3AF]">{filtered.length} POs</span>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <tr>
                      {['PO Number','Supplier','Sub Code','Type','Status','Committed Exc.VAT','IPC','Store','Equip.','Total Invoiced','Remaining'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {filtered.map(r => {
                      const pct = r.committed > 0 ? (r.totalInvoiced / r.committed) * 100 : 0
                      const isOver = r.diff < -1
                      return (
                        <tr key={r.id} className={`hover:bg-[#FAFAFA] ${isOver ? 'bg-red-50' : ''}`}>
                          <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1A7A4A] whitespace-nowrap">{r.po_number}</td>
                          <td className="px-4 py-3 text-[#2E2C29] max-w-xs truncate" title={r.vendor}>{r.vendor}</td>
                          <td className="px-4 py-3 font-mono text-xs text-[#6B7280]">{r.sub_code || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${TYPE_COLORS[r.po_type] || 'bg-slate-100 text-[#6B7280]'}`}>
                              {r.po_type}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === 'approved' ? 'bg-[#DDD9D0] text-[#1A7A4A]' : 'bg-slate-100 text-[#6B7280]'}`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-[#1C1A18] whitespace-nowrap">{fmt(r.committed)}</td>
                          <td className="px-4 py-3 text-right text-[#1A7A4A] whitespace-nowrap">{r.ipc > 0 ? fmt(r.ipc) : <span className="text-slate-300">—</span>}</td>
                          <td className="px-4 py-3 text-right text-[#1A7A4A] whitespace-nowrap">{r.store > 0 ? fmt(r.store) : <span className="text-slate-300">—</span>}</td>
                          <td className="px-4 py-3 text-right text-amber-600 whitespace-nowrap">{r.eqp > 0 ? fmt(r.eqp) : <span className="text-slate-300">—</span>}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {r.totalInvoiced > 0 ? (
                              <div>
                                <span className="font-semibold text-[#1C1A18]">{fmt(r.totalInvoiced)}</span>
                                <div className="w-full bg-slate-100 rounded-full h-1 mt-1">
                                  <div className={`h-1 rounded-full ${pct > 100 ? 'bg-red-500' : pct > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                    style={{ width: `${Math.min(pct, 100)}%` }}/>
                                </div>
                                <span className="text-xs text-[#9CA3AF]">{pct.toFixed(1)}%</span>
                              </div>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span className={`font-semibold ${isOver ? 'text-red-600' : r.diff === r.committed ? 'text-[#9CA3AF]' : 'text-[#2E2C29]'}`}>
                              {fmt(r.diff)}
                            </span>
                            {isOver && <span className="block text-xs text-red-400">Over budget</span>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  {/* Totals Row */}
                  <tfoot className="bg-[#F5F1EA] border-t-2 border-slate-300">
                    <tr>
                      <td colSpan={5} className="px-4 py-3 text-xs font-bold text-[#6B7280] uppercase">Total ({filtered.length} POs)</td>
                      <td className="px-4 py-3 text-right font-bold text-[#1C1A18] whitespace-nowrap">{fmt(totals.committed)}</td>
                      <td className="px-4 py-3 text-right font-bold text-[#1A7A4A] whitespace-nowrap">{fmt(totals.ipc)}</td>
                      <td className="px-4 py-3 text-right font-bold text-[#1A7A4A] whitespace-nowrap">{fmt(totals.store)}</td>
                      <td className="px-4 py-3 text-right font-bold text-amber-600 whitespace-nowrap">{fmt(totals.eqp)}</td>
                      <td className="px-4 py-3 text-right font-bold text-[#1C1A18] whitespace-nowrap">{fmt(totals.totalInvoiced)}</td>
                      <td className={`px-4 py-3 text-right font-bold whitespace-nowrap ${totals.diff < 0 ? 'text-red-600' : 'text-[#1C1A18]'}`}>{fmt(totals.diff)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}

        {!loading && selectedProject && rows.length === 0 && (
          <div className="text-center py-20 text-[#9CA3AF] text-sm">No POs found for this project.</div>
        )}

        {!selectedProject && (
          <div className="text-center py-20 text-[#9CA3AF] text-sm">Select a project to start.</div>
        )}
      </div>
    </div>
  )
}
